import {Injectable} from '@angular/core';
import * as _ from 'lodash-es';
import {defer, EMPTY, merge, Observable, of, Subject} from 'rxjs';
import {filter, share, tap} from 'rxjs/operators';
import {bindWith} from '../../common/rxjs/bind-with';
import {AssetService} from '../../components/asset';
import {WebSocketMessage} from '../../components/streaming/streaming-interfaces';
import {SubscriptionStreamingService} from '../../components/streaming/subscription-streaming.service';
import {SubscriptionSubjectType} from '../../components/streaming/subscription-subject-type';
import {AssetValue} from '../../models/asset';
import {ExchangeAccountId} from '../../models/exchange-account';

/**
 * AccountBalance interface with used/known only properties.
 */
export interface AccountBalance {
    accountId: ExchangeAccountId;
    balances: AssetValue[];
}

export enum UserDataWebSocketMessageType {
    ACCOUNT_BALANCE   = 'ACCOUNT_BALANCE',
    ORDER             = 'ORDER',
    ORDER_SNAPSHOT    = 'ORDER_SNAPSHOT',
    POSITION          = 'POSITION',
    POSITION_SNAPSHOT = 'POSITION_SNAPSHOT',
    INVALID_ACCOUNT   = 'INVALID_ACCOUNT'
}

export interface UserDataWebSocketMessage extends WebSocketMessage {
    messageType: UserDataWebSocketMessageType;
}

interface AccountBalanceWebSocketMessage extends UserDataWebSocketMessage {
    messageType: UserDataWebSocketMessageType.ACCOUNT_BALANCE;
    data: AccountBalance;
}

interface AccountBalanceByAccountId {
    [accountId: number]: AccountBalance;
}

interface UserDataCoworker {
    handleMessage(message: UserDataWebSocketMessage): void;
    cleanUp?(): void;
}

@Injectable()
export class UserDataStreamingService {

    public readonly messages$: Observable<UserDataWebSocketMessage>;
    public readonly accountBalance$: Observable<AccountBalance>;
    public readonly subscriptionSent$: Observable<any>;
    public readonly disconnected$: Observable<any>;

    private subscriptionSentSubject = new Subject<any>();
    private disconnectedSubject = new Subject<any>();

    private subscriptionSubjectType: SubscriptionSubjectType<UserDataWebSocketMessage> = {
        name: 'USER_DATA',
        operations: {
            SUBSCRIBE: 'SUBSCRIBE_USER_DATA',
            UNSUBSCRIBE: 'UNSUBSCRIBE_USER_DATA'
        },
        messagePipe: _.flow(
            filter((message: WebSocketMessage) => message.messageType in UserDataWebSocketMessageType),
            tap((message: UserDataWebSocketMessage): void => this.readUserDataMessage(message))
        ),
        subscriptionSent$: this.subscriptionSentSubject,
        disconnected$: this.disconnectedSubject,
        deprecateSubject: () => this.deprecateSubject()
    };

    private accountBalancesDict: AccountBalanceByAccountId;
    private accountsBalances: AccountBalance[];

    private readonly accountBalanceSubject$ = new Subject<AccountBalance>();

    private readonly userDataCoworkers: UserDataCoworker[] = [];

    constructor(
        private subscriptionStreamingService: SubscriptionStreamingService,
        private assetService: AssetService
    ) {
        this.messages$ = this.createMessagesStream();
        this.accountBalance$ = this.createAccountBalanceStream();
        this.subscriptionSent$ = this.subscriptionSentSubject.asObservable();
        this.disconnected$ = this.disconnectedSubject.asObservable();
    }

    public registerCoworker(coworker: UserDataCoworker): void {
        this.userDataCoworkers.push(coworker);
    }

    private createMessagesStream(): Observable<any> {
        return this.subscriptionStreamingService.createSubscription(this.subscriptionSubjectType, [null]).pipe(
            share()
        );
    }

    private readUserDataMessage(message: UserDataWebSocketMessage): void {
        switch (message.messageType) {
            case UserDataWebSocketMessageType.ACCOUNT_BALANCE:
                this.readAccountBalanceMessage(message as AccountBalanceWebSocketMessage);
                break;
        }

        this.userDataCoworkers.forEach(coworker => coworker.handleMessage(message));
    }

    private readAccountBalanceMessage(message: AccountBalanceWebSocketMessage): void {
        let resultAccountBalance: AccountBalance;

        const accountBalanceUpdate: AccountBalance = message.data;
        const accountId = accountBalanceUpdate.accountId;

        const knownAccountBalance = this.accountBalancesDict
            && this.accountBalancesDict[accountId];

        if (knownAccountBalance) {
            this.updateAssetValues(knownAccountBalance.balances, accountBalanceUpdate.balances);

            resultAccountBalance = knownAccountBalance;
        } else {
            this.accountBalancesDict = this.accountBalancesDict || {};
            this.accountBalancesDict[accountId] = accountBalanceUpdate;

            this.accountsBalances = this.accountsBalances || [];
            this.accountsBalances.push(accountBalanceUpdate);

            resultAccountBalance = accountBalanceUpdate;
        }

        this.cacheAssetsFromAccountBalance(resultAccountBalance);
        this.accountBalanceSubject$.next(resultAccountBalance);
    }

    private deprecateSubject(): void {
        this.userDataCoworkers.forEach(coworker => {
            if (coworker.cleanUp) {
                coworker.cleanUp();
            }
        });

        this.accountBalancesDict = null;
        this.accountsBalances = null;
    }

    private createAccountBalanceStream(): Observable<AccountBalance> {
        const initialAccountsBalances$ = defer((): Observable<AccountBalance> =>
            this.accountsBalances && this.accountsBalances.length
                ? of(...this.accountsBalances)
                : EMPTY
        );

        return merge(
            initialAccountsBalances$,
            this.accountBalanceSubject$.pipe(
                bindWith(this.messages$)
            )
        );
    }

    private updateAssetValues(assetValues: AssetValue[], assetValuesUpdate: AssetValue[]): void {
        const newAssetValues: AssetValue[] = [];

        assetValuesUpdate.forEach(assetValueUpdate => {
            const knowsAssetValueIndex = assetValues.findIndex(assetValue =>
                assetValue.asset.id === assetValueUpdate.asset.id
            );

            if (knowsAssetValueIndex >= 0) {
                assetValues[knowsAssetValueIndex] = assetValueUpdate;
            } else {
                newAssetValues.push(assetValueUpdate);
            }
        });

        assetValues.push(...newAssetValues);
    }

    private cacheAssetsFromAccountBalance(accountBalance: AccountBalance): void {
        accountBalance.balances.forEach(balance =>
            balance.asset = this.assetService.syncAssetWithCache(balance.asset)
        );
    }

}
